// =============================================================================
// features/documents/api.ts — Cliente HTTP do módulo de documentos (F46-S08).
//
// Consome as rotas da F46-S04 (apps/api/src/modules/documents/routes.ts) e os
// schemas Zod PÚBLICOS de `@elemento/shared-schemas` — nenhum tipo é
// redigitado aqui (contrato do slot).
//
// Endpoints cobertos:
//   GET    /api/leads/:id/documents     — central do lead (scope='lead')
//   GET    /api/documents               — filtrado (scope='analysis'|'contract')
//   GET    /api/documents/types         — catálogo (só tipos ativos) para o
//                                          seletor de tipo do upload
//   POST   /api/documents/signed-url    — passo 1 do upload
//   PUT    <uploadUrl>                  — passo 2, direto no storage (XHR)
//   POST   /api/documents               — passo 3, confirma o upload
//   GET    /api/documents/:id/download  — passo único de download (302 → blob)
//   DELETE /api/documents/:id           — soft delete
//
// `GET /api/documents/types` (F46-S19) é gateada por `documents:write` — a
// mesma permissão que já concede upload (admin, gestor_geral,
// gestor_regional, agente, operador, cobrança) — e sempre devolve só tipos
// ativos, sem aceitar query params. A rota `/api/admin/document-types`
// (documents:manage, admin-only) continua existindo para a administração do
// catálogo (inclui inativos), mas não é usada aqui.
//
// LGPD (doc 17 §8.3): `original_filename` é PII — nunca logado em
// console/telemetria aqui. `storage_key` nunca é solicitada nem armazenada no
// cliente (o schema de resposta já a omite deliberadamente).
// =============================================================================

import type {
  Document,
  DocumentCreate,
  DocumentListResponse,
  DocumentType,
  SignedUrlRequest,
  SignedUrlResponse,
} from '@elemento/shared-schemas';
import {
  DocumentListResponseSchema,
  DocumentSchema,
  DocumentTypeListResponseSchema,
  SignedUrlResponseSchema,
} from '@elemento/shared-schemas';
import { z } from 'zod';

import { api, ApiError } from '../../lib/api';
import { useAuthStore } from '../../lib/auth-store';

// ─── Erro tipado para o gate de permissão de download ──────────────────────

/** Lançado quando o backend recusa `documents:download` (403) — a UI trata
 * como "sem permissão", nunca como erro genérico de rede. */
export class DocumentDownloadForbiddenError extends Error {
  constructor() {
    super('Você não tem permissão para baixar este documento.');
    this.name = 'DocumentDownloadForbiddenError';
  }
}

// ─── GET /api/leads/:id/documents — scope='lead' ────────────────────────────

const leadDocumentsResponseSchema = z.object({ data: z.array(DocumentSchema) });

export async function fetchLeadDocuments(leadId: string): Promise<Document[]> {
  const raw = await api.get<unknown>(`/api/leads/${encodeURIComponent(leadId)}/documents`);
  return leadDocumentsResponseSchema.parse(raw).data;
}

// ─── GET /api/documents — scope='analysis'|'contract' ───────────────────────

export interface DocumentsListFilter {
  leadId: string;
  analysisId?: string;
  contractId?: string;
  /** Teto do backend é 100 (documentsListQuerySchema) — suficiente para o
   * volume de documentos de uma única análise/contrato. */
  limit?: number;
}

export async function fetchDocuments(filter: DocumentsListFilter): Promise<DocumentListResponse> {
  const qs = new URLSearchParams();
  qs.set('leadId', filter.leadId);
  if (filter.analysisId) qs.set('analysisId', filter.analysisId);
  if (filter.contractId) qs.set('contractId', filter.contractId);
  qs.set('limit', String(filter.limit ?? 100));

  const raw = await api.get<unknown>(`/api/documents?${qs.toString()}`);
  return DocumentListResponseSchema.parse(raw);
}

// ─── GET /api/documents/types — catálogo do seletor de tipo ────────────────

/**
 * Busca o catálogo de tipos de documento ativos, disponível para qualquer
 * papel com `documents:write` (F46-S19). 403 ainda é tratado como catálogo
 * vazio (defesa em profundidade caso a permissão mude no backend), mas na
 * prática quem chama esta função já passou pelo gate de `documents:write`
 * na UI. Outros erros propagam.
 */
export async function fetchDocumentTypesCatalog(): Promise<DocumentType[]> {
  try {
    const raw = await api.get<unknown>('/api/documents/types');
    return DocumentTypeListResponseSchema.parse(raw).data;
  } catch (err) {
    if (err instanceof ApiError && err.status === 403) {
      return [];
    }
    throw err;
  }
}

// ─── Opções de vínculo do upload (F46-S20) ──────────────────────────────────
//
// Quando o upload parte da ficha do lead (scope='lead'), o operador pode
// escolher OPCIONALMENTE vincular o documento a uma análise ou contrato
// daquele mesmo lead — nunca busca livre entre leads (vazamento entre
// cidadãos). Os módulos de análise (`features/credit-analyses`) e contrato
// (`features/contracts`) são proibidos neste slot; por isso os schemas abaixo
// são locais e mínimos — só os campos usados no rótulo do seletor, nunca o
// contrato completo dessas rotas.

/** Lançado quando o backend recusa (403) a permissão de leitura exigida para
 * montar o seletor de vínculo — a UI trata como aviso visível, nunca como
 * lista vazia silenciosa (nota do slot: mesmo bug do simulador manual sem
 * `credit_products:read`). */
export class LeadLinkOptionsForbiddenError extends Error {
  constructor(resource: string) {
    super(`Você não tem permissão para ver ${resource} deste lead.`);
    this.name = 'LeadLinkOptionsForbiddenError';
  }
}

const leadAnalysisOptionSchema = z.object({
  id: z.string().uuid(),
  status: z.string(),
  created_at: z.string(),
});

const leadAnalysesForSelectorResponseSchema = z.object({
  data: z.array(leadAnalysisOptionSchema),
});

export interface LeadAnalysisOption {
  id: string;
  status: string;
  created_at: string;
}

/**
 * GET /api/leads/:leadId/credit-analyses — análises do próprio lead, para o
 * seletor de vínculo do upload. Permissão: `credit_analyses:read`.
 */
export async function fetchLeadAnalysesForSelector(leadId: string): Promise<LeadAnalysisOption[]> {
  try {
    const raw = await api.get<unknown>(
      `/api/leads/${encodeURIComponent(leadId)}/credit-analyses?limit=100`,
    );
    return leadAnalysesForSelectorResponseSchema.parse(raw).data;
  } catch (err) {
    if (err instanceof ApiError && err.status === 403) {
      throw new LeadLinkOptionsForbiddenError('as análises');
    }
    throw err;
  }
}

const leadCustomerIdResponseSchema = z.object({ customer_id: z.string().uuid().nullable() });

const contractOptionSchema = z.object({
  id: z.string().uuid(),
  contract_reference: z.string(),
  status: z.string(),
  created_at: z.string(),
});

const contractsForSelectorResponseSchema = z.object({
  data: z.array(contractOptionSchema),
});

export interface ContractOption {
  id: string;
  contract_reference: string;
  status: string;
  created_at: string;
}

/**
 * Contratos do próprio lead, para o seletor de vínculo do upload.
 *
 * Não existe `GET /api/contracts?leadId=` — contratos são filtrados por
 * `customer_id` (o cliente convertido a partir do lead). Por isso o primeiro
 * passo busca `GET /api/leads/:id` para obter `customer_id` (F17-S08); um
 * lead ainda não convertido (`customer_id === null`) simplesmente não tem
 * contratos possíveis — devolve lista vazia sem chamar `/api/contracts`.
 * Permissões: `leads:read` (implícita — o operador já está na ficha do
 * lead) e `contracts:read`.
 */
export async function fetchLeadContractsForSelector(leadId: string): Promise<ContractOption[]> {
  try {
    const leadRaw = await api.get<unknown>(`/api/leads/${encodeURIComponent(leadId)}`);
    const { customer_id: customerId } = leadCustomerIdResponseSchema.parse(leadRaw);
    if (!customerId) return [];

    const raw = await api.get<unknown>(
      `/api/contracts?customer_id=${encodeURIComponent(customerId)}&limit=100`,
    );
    return contractsForSelectorResponseSchema.parse(raw).data;
  } catch (err) {
    if (err instanceof ApiError && err.status === 403) {
      throw new LeadLinkOptionsForbiddenError('os contratos');
    }
    throw err;
  }
}

// ─── POST /api/documents/signed-url — passo 1 ───────────────────────────────

export async function createDocumentSignedUrl(body: SignedUrlRequest): Promise<SignedUrlResponse> {
  const raw = await api.post<unknown>('/api/documents/signed-url', body);
  return SignedUrlResponseSchema.parse(raw);
}

// ─── PUT <uploadUrl> — passo 2, direto no storage privado (XHR p/ progresso) ─

export interface UploadToSignedUrlParams {
  uploadUrl: string;
  file: File | Blob;
  mime: string;
  onProgress?: (percent: number) => void;
  signal?: AbortSignal;
}

/**
 * PUT direto no storage via XHR — `fetch` não expõe progresso de upload
 * (`onprogress`), por isso XHR aqui é deliberado (mesmo molde de
 * features/quick-replies/useUploadQuickReplyMedia.ts). Sem Authorization —
 * a URL já é pré-assinada.
 */
export function uploadToSignedUrl(params: UploadToSignedUrlParams): Promise<void> {
  const { uploadUrl, file, mime, onProgress, signal } = params;

  return new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    const onAbort = (): void => {
      xhr.abort();
    };
    if (signal) {
      if (signal.aborted) {
        reject(new Error('Upload cancelado.'));
        return;
      }
      signal.addEventListener('abort', onAbort);
    }

    const cleanup = (): void => {
      if (signal) signal.removeEventListener('abort', onAbort);
    };

    xhr.open('PUT', uploadUrl, true);
    xhr.setRequestHeader('Content-Type', mime);

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    };

    xhr.onload = () => {
      cleanup();
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress?.(100);
        resolve();
      } else {
        reject(new Error(`Upload falhou (HTTP ${xhr.status}).`));
      }
    };

    xhr.onerror = () => {
      cleanup();
      reject(new Error('Erro de rede durante o upload. Verifique sua conexão.'));
    };

    xhr.onabort = () => {
      cleanup();
      reject(new Error('Upload cancelado.'));
    };

    xhr.send(file);
  });
}

// ─── POST /api/documents — passo 3 ──────────────────────────────────────────

export async function attachDocument(
  body: DocumentCreate,
  idempotencyKey: string,
): Promise<Document> {
  const raw = await api.post<unknown>('/api/documents', body, {
    headers: { 'Idempotency-Key': idempotencyKey },
  });
  return DocumentSchema.parse(raw);
}

// ─── DELETE /api/documents/:id — soft delete ────────────────────────────────

export async function deleteDocument(documentId: string): Promise<void> {
  await api.delete<void>(`/api/documents/${encodeURIComponent(documentId)}`);
}

// ─── GET /api/documents/:id/download — 302 → blob ───────────────────────────
//
// A rota responde 302 para uma URL assinada de storage (TTL 5min). O cliente
// genérico `api.*` faz `.json()` — inadequado aqui (resposta final é binária).
// Segue o mesmo molde de features/relatorios/api.ts (postReportsExport):
// fetch manual com Authorization no primeiro hop; o browser descarta o header
// Authorization ao seguir redirect cross-origin para o storage (comportamento
// padrão do fetch), o que é seguro porque a URL de destino já é pré-assinada.

const API_BASE = (import.meta.env['VITE_API_URL'] as string | undefined) ?? 'http://localhost:3333';

function getCsrfTokenForDownload(): string {
  const match = document.cookie.split('; ').find((row) => row.startsWith('csrf_token='));
  return match ? (match.split('=')[1] ?? '') : '';
}

export interface DocumentDownloadResult {
  blob: Blob;
  contentType: string;
}

/**
 * Baixa o binário do documento via GET /api/documents/:id/download.
 * Lança `DocumentDownloadForbiddenError` em 403 (sem `documents:download`) —
 * a UI trata isso como estado de permissão, não como erro de rede.
 */
export async function downloadDocumentBlob(documentId: string): Promise<DocumentDownloadResult> {
  const accessToken = useAuthStore.getState().accessToken;
  const headers: Record<string, string> = {};
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
  const csrf = getCsrfTokenForDownload();
  if (csrf) headers['X-CSRF-Token'] = csrf;

  const res = await fetch(`${API_BASE}/api/documents/${encodeURIComponent(documentId)}/download`, {
    method: 'GET',
    headers,
    credentials: 'include',
  });

  if (res.status === 403) {
    throw new DocumentDownloadForbiddenError();
  }
  if (!res.ok) {
    throw new Error(`Não foi possível baixar o documento (HTTP ${res.status}).`);
  }

  const blob = await res.blob();
  return { blob, contentType: res.headers.get('Content-Type') ?? 'application/octet-stream' };
}

/** Dispara o download do blob no browser (mesmo padrão de useExportReport.ts). */
export function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = window.document.createElement('a');
  a.href = url;
  a.download = filename;
  window.document.body.appendChild(a);
  a.click();
  window.document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
